/**
 * Reconocimiento del tipo de un campo por su NOMBRE.
 *
 * <b>Por qué es un módulo y no una lista por archivo.</b> `tableColumns` y `suggestChartType`
 * implementaban la misma idea —adivinar la naturaleza de un campo por cómo se llama— con su
 * propia lista, su propio `normalize` y el mismo defecto: comparaban por <b>substring</b>. Eso
 * hacía que `dias_sin_entrada` casara con `dia` y se tipara como FECHA (la celda pintaba
 * «1 ene 1970» para un conteo de días), que `total_registros` casara con `total` y saliera como
 * moneda, y que `rango` o `tamano` casaran con `ano` y pasaran por temporales.
 *
 * <b>Aquí se compara por PALABRA.</b> El nombre se parte por los separadores habituales
 * (`_`, `-`, camelCase) y la pista tiene que ser una de esas palabras completas. `dias_sin_entrada`
 * ya no es `dia`, y `fecha_emision` sigue siendo `fecha`.
 */

/** Quita acentos y baja a minúsculas, para que `año` y `ano` sean lo mismo. */
export function normalizeFieldName(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Las palabras que componen un nombre de campo.
 *
 * `STOCK_ACTUAL` → `['stock','actual']` · `fechaEmision` → `['fecha','emision']` ·
 * `dias_sin_entrada` → `['dias','sin','entrada']`.
 */
export function fieldWords(value: string): string[] {
  return normalizeFieldName(value.replace(/([a-z0-9])([A-Z])/g, '$1 $2'))
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** Si alguna de las pistas aparece como PALABRA completa del nombre del campo. */
export function matchesHint(fieldName: string, hints: readonly string[]): boolean {
  const words = fieldWords(fieldName);
  return hints.some(hint => words.includes(hint));
}

/**
 * Si un nombre de campo designa un IDENTIFICADOR y no un dato que se pueda graficar.
 *
 * <b>No vale `name.includes('id')`.</b> Ese predicado —que es el que había— descarta
 * `cantidad`, `unidad`, `validado` y `liquidez`, porque las cuatro contienen la secuencia
 * «id». En un ERP en español eso significa saltarse justamente la columna que se quería medir:
 * un sparkline sobre una serie de `cantidad` se quedaba sin datos, en silencio.
 *
 * Se reconoce `id` como palabra propia (`id`, `widget_id`, `ID`, `idCliente`) y los `uuid`/`guid`.
 */
export function isIdentifierField(fieldName: string): boolean {
  const words = fieldWords(fieldName);
  return words.some(w => w === 'id' || w === 'ids' || w === 'uuid' || w === 'guid');
}
